/**
 * The "research preview" sticker (owner artwork, 2026-08-11).
 *
 * ## What it is for
 *
 * Expectation-setting, in the one place a first-time visitor is looking. The
 * tool is solid and its quantization reading is still growing — all three open
 * reports are about exactly that — so "research preview" is honest rather than
 * falsely modest, and it says *why* to expect rough edges instead of merely
 * labelling a stage the way "beta" does.
 *
 * It is amber on purpose, and the amber is `--report-action`'s. That links two
 * ideas visually: **this is a preview**, and **tell us what is wrong**. The
 * sticker and the Report issue button are one family.
 *
 * ## Why the colours stay literal here
 *
 * Same exemption the logo has, and this is its fourth member. The sticker is an
 * opaque object: it carries its own contrast and therefore works on both
 * palettes with no theming at all, which is the whole reason a sticker is the
 * right metaphor. Wiring it to tokens would make it change colour with the
 * page and stop reading as a physical thing stuck onto one.
 *
 * The consequence is the logo's consequence: if the amber ever moves,
 * `--report-action` in `tokens.css` and these fills move together or the
 * family breaks.
 *
 * ## Why the text is `textLength`-constrained
 *
 * The original used `Arial, Helvetica, sans-serif` with hand-tuned
 * `letter-spacing`, and the underline beneath PREVIEW is a **fixed path**. Those
 * two facts do not survive contact with other people's machines: the stack
 * resolves to Arial on Windows, Helvetica on macOS and Liberation Sans on
 * Linux, and a few percent of width variance leaves that line sitting under
 * nothing. Invisible to whoever drew it; visible to a visitor.
 *
 * `textLength` with `lengthAdjust="spacing"` pins each line to an exact width
 * and distributes the difference between letters. The glyphs keep their shapes,
 * the underline always lines up, and the face can be the site's own display
 * font — which is `--font-display` (Archivo Variable, self-hosted), inherited
 * because this is inline SVG in the document rather than an `<img>`.
 *
 * That also covers the moment before the woff2 lands: the fallback renders at
 * the same width, so nothing shifts when Archivo arrives.
 *
 * ## Every id is namespaced
 *
 * Inline SVG shares the document's id space. The artwork arrived with
 * `id="title"` and `id="desc"`, which are about as collision-prone as ids get —
 * duplicates are invalid HTML and break `aria-labelledby` resolution.
 * `discovery.ts` namespaces its own for the same reason.
 *
 * Pure: returns markup, touches no DOM, holds no state.
 */

/**
 * The knobs live in CSS, not in this markup.
 *
 * Rotation, width and offset are custom properties on `.beta-sticker` so they
 * can be turned in devtools on a running page and read straight back into
 * `app.css` — see the block there. A `transform` attribute baked in here would
 * make every adjustment a rebuild.
 */
export function betaStickerHtml(): string {
  return `<svg class="beta-sticker" viewBox="0 0 460 210" fill="none" role="img"
    aria-labelledby="beta-sticker-title beta-sticker-desc" focusable="false">
    <title id="beta-sticker-title">Research preview</title>
    <desc id="beta-sticker-desc">A hand-drawn amber sticker reading “Research preview — lens calibration in progress”, with a small smiling magnifying lens.</desc>
    <defs>
      <filter id="beta-sticker-shadow" x="0" y="0" width="460" height="210"
        filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
        <feDropShadow dx="0" dy="9" stdDeviation="6" flood-color="#422C08" flood-opacity=".2"/>
      </filter>
    </defs>

    <g filter="url(#beta-sticker-shadow)">
      <!-- Deliberately imperfect edges: a sticker, not a badge. -->
      <path d="M29 36c1-10 9-16 20-16h352c12 0 20 7 20 18l-2 111c0 10-8 17-20 17H48c-12 0-19-7-20-17l1-113Z"
        fill="#F7C95B" stroke="#8B480F" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/>
      <path d="M40 40c3-7 10-11 19-11h328c11 0 18 4 21 11" stroke="#FFE9A9" stroke-width="3" fill="none" stroke-linecap="round"/>

      <!-- The lens, which is the wordmark's lens and the robot's lens. -->
      <g stroke="#513B1C" stroke-linecap="round" stroke-linejoin="round" transform="translate(10,-15)">
        <path d="m108 120 27 27" stroke-width="11"/>
        <path d="m108 120 27 27" stroke="#F2AB31" fill="none" stroke-width="6"/>
        <circle cx="78" cy="91" r="39" fill="#FFEAB0" stroke-width="4"/>
        <path d="M55 73c6-8 13-12 22-13" stroke="#FFF8D8" fill="none" stroke-width="4"/>
        <circle cx="65" cy="91" r="4" fill="#513B1C" stroke="none"/>
        <circle cx="91" cy="91" r="4" fill="#513B1C" stroke="none"/>
        <path d="M68 104c6 7 15 7 21 0" fill="none" stroke-width="3"/>
      </g>

      <!--
        textLength on every line, so the layout is a property of the artwork
        rather than of whichever face the machine happens to resolve. The
        underline below is a fixed path and only lines up because of this.
      -->
      <text x="155" y="73" textLength="218" lengthAdjust="spacing"
        fill="#432E0F" font-size="36" font-weight="800">RESEARCH</text>
      <text x="153" y="120" textLength="250" lengthAdjust="spacing"
        fill="#432E0F" font-size="52" font-weight="800">PREVIEW</text>
      <path d="M155 134c53 3 155 3 250-1" stroke="#A76216" stroke-width="3"
        fill="none" stroke-linecap="round" opacity=".75"/>
      <text x="65" y="155" textLength="330" lengthAdjust="spacing"
        fill="#684613" font-size="18" font-weight="700">LENS CALIBRATION IN PROGRESS</text>
    </g>
  </svg>`;
}
